import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
} from '@mui/material';
import LastUpdated from '../components/LastUpdated';
import {
  TrendingUp,
  AttachMoney,
  Assessment,
  ShowChart,
} from '@mui/icons-material';
import { fetchDashboardStats, fetchActiveData } from '../store/dashboardSlice';
import { CryptoIcon, ExchangeIcon } from '../components/IconDisplay';
import { formatDecimal } from '../utils/dateUtils';
import { PAGE_REFRESH_MS } from '../config/intervals';

function StatCard({ title, value, icon, color, subtitle }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h5">{value}</Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const dispatch = useDispatch();
  const { tradeStats, pnlSummary, activeTriggers, activeTradingPairs, loading } = useSelector((state) => state.dashboard);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  useEffect(() => {
    const refreshAll = async () => {
      await Promise.all([
        dispatch(fetchDashboardStats()),
        dispatch(fetchActiveData()),
      ]);
      setLastRefreshedAt(Date.now());
    };
    refreshAll();

    const interval = setInterval(async () => {
      await dispatch(fetchActiveData());
      setLastRefreshedAt(Date.now());
    }, PAGE_REFRESH_MS);

    return () => clearInterval(interval);
  }, [dispatch]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const formatNumber = (num) => {
    const value = Number(num || 0);
    if (isNaN(value)) return '0.00';
    
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}K`;
    }
    return value.toFixed(2);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <LastUpdated timestamp={lastRefreshedAt} />
      </Box>
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Trades"
            value={tradeStats?.total_trades || 0}
            subtitle={`Buy: ${tradeStats?.buy_trades || 0} | Sell: ${tradeStats?.sell_trades || 0}`}
            icon={<Assessment />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Volume (USDT)"
            value={`$${formatNumber(tradeStats?.total_volume_usdt || 0)}`}
            subtitle={`Avg Price: $${formatNumber(tradeStats?.avg_price || 0)}`}
            icon={<AttachMoney />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total PnL (USDT)"
            value={`$${formatNumber(pnlSummary?.total_pnl || 0)}`}
            subtitle={`Avg Premium: ${formatDecimal(pnlSummary?.avg_premium || 0, 2)}%`}
            icon={<TrendingUp />}
            color={(pnlSummary?.total_pnl || 0) >= 0 ? 'success.main' : 'error.main'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Triggers"
            value={activeTriggers?.length || 0}
            subtitle={`Trading Pairs: ${activeTradingPairs?.length || 0}`}
            icon={<ShowChart />}
            color="warning.main"
          />
        </Grid>

        {/* Active Triggers */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '400px', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Active Triggers
            </Typography>
            {(!activeTriggers || activeTriggers.length === 0) ? (
              <Typography color="textSecondary">No active triggers</Typography>
            ) : (
              activeTriggers.map((trigger) => (
                <Box key={trigger.uuid} sx={{ mb: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <CryptoIcon symbol={trigger.trading_pair_target_symbol || trigger.trading_pair_symbol} size={20} />
                    <Typography variant="subtitle1">
                      {trigger.trading_pair_display}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      Low: {trigger.low} | High: {trigger.high}
                    </Typography>
                    <Chip
                      label={trigger.status}
                      color={trigger.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        {/* Active Trading Pairs */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '400px', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Active Trading Pairs
            </Typography>
            {(!activeTradingPairs || activeTradingPairs.length === 0) ? (
              <Typography color="textSecondary">No active trading pairs</Typography>
            ) : (
              activeTradingPairs.slice(0, 10).map((pair) => (
                <Box key={pair.id} sx={{ mb: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <CryptoIcon symbol={pair.target_symbol || pair.symbol} size={20} />
                    <Typography variant="subtitle1">
                      {pair.symbol}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                    <ExchangeIcon exchangeName={pair.target_exchange_name} size={16} />
                    <Typography variant="body2" color="textSecondary">
                      Target: {pair.target_exchange_name} {pair.target_market_name} ({pair.target_quote_asset})
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ExchangeIcon exchangeName={pair.origin_exchange_name} size={16} />
                    <Typography variant="body2" color="textSecondary">
                      Origin: {pair.origin_exchange_name} {pair.origin_market_name} ({pair.origin_quote_asset})
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        {/* Summary Stats */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Financial Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Total Fees Paid</Typography>
                  <Typography variant="h6">
                    ${formatNumber((tradeStats?.total_fees || 0) + (pnlSummary?.total_fee || 0))}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">PnL Trade Count</Typography>
                  <Typography variant="h6">
                    {pnlSummary?.trade_count || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Average Premium</Typography>
                  <Typography variant="h6">
                    {formatDecimal(pnlSummary?.avg_premium || 0)}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Net Profit</Typography>
                  <Typography variant="h6" color={((pnlSummary?.total_pnl || 0) - (pnlSummary?.total_fee || 0)) >= 0 ? '#4caf50' : '#f44336'}>
                    ${formatNumber((pnlSummary?.total_pnl || 0) - (pnlSummary?.total_fee || 0))}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;